import dayjs from "dayjs";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  MeasurementsConfig,
  MeasurementTypes,
} from "../../../../@types/Measurements";
import { Notification } from "../../../../@types/Notification";
import { ISectionProps } from "../../../../@types/Patient";
import { ChevronDown } from "../../../../assets/icons/ChevronDown";
import { ChevronUp } from "../../../../assets/icons/ChevronUp";
import { CustomIconButton } from "../../../../components/CustomIconButton";
import { useMeasurementConfigs } from "../../../../hooks/queries/measurement-configs";
import useIsMobile from "../../../../hooks/useIsMobile";
import { useModal } from "../../../../hooks/useModal";
import { useToast } from "../../../../hooks/useToast";
import { useUserPreferences } from "../../../../hooks/useUserPreferences";
import { PageTitle } from "../../../../layout/components/PageTitle";
import { mixpanelActions } from "../../../../utils/mixpanel";
import {
  ChartsContainer,
  MobilePatientPageTitleContainer,
} from "../../PatientPage.styles";
import { AddMeasurementModal } from "../AddMeasurementModal";
import { MeasurementCard } from "../MeasurementCard";
import { AddMeasurementButton } from "../MeasurementsSection/MeasurementSection.styles";
import { NewDataCOntainer, TitleContainer } from "./LabsSection.styles";

interface ILabsSectionProps extends ISectionProps {
  configs?: MeasurementsConfig[];
}

export function LabsSection({
  patient,
  updateColapsedSections,
  collapsedSection,
  configs: propConfigs,
}: ILabsSectionProps) {
  const { t } = useTranslation();

  // All hooks must be called unconditionally at the top
  const { portalPreferences } = useUserPreferences();
  const { isMobile } = useIsMobile();
  const { openModal, closeModal } = useModal();
  const { showToast } = useToast();
  const [showNewData, setShowNewData] = useState<boolean>(false);

  // Use props if provided, otherwise fetch via hook (for backwards compatibility)
  const { configs: hookConfigs } = useMeasurementConfigs({
    patientId: propConfigs ? undefined : patient?.id,
    section: "labResults",
  });

  const configs = propConfigs ?? hookConfigs;

  const handleOnModalClose = (notification?: Notification) => {
    if (notification !== undefined) {
      showToast(notification.message, notification.type);

      setTimeout(() => {
        window.location.reload();
      }, 500);
    }
    closeModal();
  };

  const handleAddLabResult = () => {
    mixpanelActions.track(`User action: Add Measurement Button`);
    openModal(
      <AddMeasurementModal
        section="lab_result"
        patientId={patient?.id.toString()}
        onClose={handleOnModalClose}
      />,
      {
        width: isMobile ? "98vw" : "620px",
        height: isMobile ? undefined : "auto",
        borderRadius: isMobile ? "8px" : undefined,
        showCloseButton: true,
      }
    );
  };

  const checkNewData = (mostRecentDate: dayjs.Dayjs) => {
    if (showNewData === false) {
      const collapsedDate = dayjs(collapsedSection.collapsedDate);
      if (mostRecentDate > collapsedDate) {
        setShowNewData(true);
      }
    }
  };

  const handleConfigs = () => {
    if (configs === null || configs.length === 0 || (configs as any).errors)
      return null;

    const configToReturn: any[] = [];

    configs
      .filter(
        (measurement) =>
          measurement.type !== portalPreferences.pinnedMeasurementType &&
          !measurement.type.startsWith("z_score")
      )
      .forEach((measurement, index) =>
        configToReturn.push(
          <MeasurementCard
            key={`${measurement.title}_${measurement.type}_${index}`}
            cardTitle={`${measurement.title} ${
              measurement.unit ? `(${measurement.unit})` : ""
            }`}
            unit={measurement.unit}
            measurementType={measurement.type as MeasurementTypes}
            minValue={measurement.min}
            maxValue={measurement.max}
            checkNewData={checkNewData}
          />
        )
      );

    return configToReturn;
  };

  const handleCollapseSection = () => {
    updateColapsedSections("labsSection");
    if (collapsedSection.value === true) {
      setShowNewData(false);
    }
  };

  // Don't render section if no configs available
  if (!configs || configs.length === 0 || (configs as any).errors) {
    return null;
  }

  return (
    <>
      {!isMobile && (
        <TitleContainer>
          <PageTitle>{t("patientPage_sections_labResults")}</PageTitle>
          {showNewData && (
            <NewDataCOntainer>
              {t("patientPage_sections_newData")}
            </NewDataCOntainer>
          )}
          <CustomIconButton
            style={{ marginLeft: "2px" }}
            onClick={handleCollapseSection}
          >
            {collapsedSection.value ? <ChevronUp /> : <ChevronDown />}
          </CustomIconButton>
        </TitleContainer>
      )}

      <ChartsContainer
        style={{
          height: collapsedSection.value ? "0" : "",
          overflow: collapsedSection.value ? "hidden" : "",
        }}
      >
        <AddMeasurementButton onClick={handleAddLabResult}>
          {t("patientPage_labs_add")}
        </AddMeasurementButton>
        {isMobile && (
          <MobilePatientPageTitleContainer>
            <PageTitle>{t("patientPage_sections_labResults")}</PageTitle>
          </MobilePatientPageTitleContainer>
        )}

        {handleConfigs()}
      </ChartsContainer>
    </>
  );
}
